import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { authService } from '../api/auth';
import { colors } from '../../../theme';

/**
 * VerifyEmail — enter the sign-up confirmation code Cognito emailed the user.
 *
 * Reached from Register (`navigate('/verify-email', { state: { email } })`) and
 * from a Login attempt on an unconfirmed account. The email is prefilled from
 * route state; if the screen is opened directly it falls back to an editable
 * field so the flow still works.
 */
const CODE_LENGTH = 6;
const RESEND_COOLDOWN = 30;

const VerifyEmail: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = (location.state as { email?: string } | null)?.email ?? '';

  const [email, setEmail] = useState(initialEmail);
  const [code, setCode] = useState('');
  const [apiError, setApiError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const codeValid = /^\d{6}$/.test(code);
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setApiError('');
    setNotice('');
    if (!emailValid) {
      setApiError('Enter the email address you registered with.');
      return;
    }
    if (!codeValid) {
      setApiError(`Enter the ${CODE_LENGTH}-digit code from your email.`);
      return;
    }
    setLoading(true);
    try {
      await authService.verifyEmail({ email, code });
      navigate('/login', { state: { verified: true, email } });
    } catch (err: any) {
      setApiError(err.message || 'Verification failed. Check the code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setApiError('');
    setNotice('');
    if (!emailValid) {
      setApiError('Enter the email address you registered with first.');
      return;
    }
    setResending(true);
    try {
      await authService.resendConfirmation({ email });
      setNotice('A new code is on its way. Check your inbox.');
      setCooldown(RESEND_COOLDOWN);
    } catch (err: any) {
      setApiError(err.message || 'Could not resend the code. Try again shortly.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: 'calc(100vh - 140px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f7fb',
        py: 6,
        px: 2,
      }}
    >
      <Card
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 4,
          border: `1px solid ${colors.grey[200]}`,
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
        }}
      >
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ textAlign: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 900, color: colors.primary.main }}>
              CAR-IA
            </Typography>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 800, textAlign: 'center', mb: 0.5 }}>
            Verify your email
          </Typography>
          <Typography
            variant="body2"
            sx={{ textAlign: 'center', color: colors.text.secondary, mb: 3 }}
          >
            {initialEmail ? (
              <>
                We sent a {CODE_LENGTH}-digit code to <strong>{initialEmail}</strong>. Enter it
                below to activate your account.
              </>
            ) : (
              <>Enter the {CODE_LENGTH}-digit code we emailed you to activate your account.</>
            )}
          </Typography>

          {apiError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {apiError}
            </Alert>
          )}
          {notice && (
            <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
              {notice}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} noValidate>
            {!initialEmail && (
              <TextField
                label="Email address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                size="small"
                sx={{ mb: 2 }}
                autoComplete="email"
              />
            )}

            <TextField
              label="Verification code"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
              fullWidth
              size="small"
              autoFocus
              inputProps={{
                inputMode: 'numeric',
                maxLength: CODE_LENGTH,
                'data-testid': 'verify-code',
                style: { letterSpacing: '0.4em', fontWeight: 700 },
              }}
              placeholder="123456"
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading}
              data-testid="verify-submit"
              sx={{
                bgcolor: colors.primary.main,
                color: 'white',
                fontWeight: 700,
                borderRadius: 2,
                py: 1.3,
                textTransform: 'none',
                fontSize: '0.95rem',
                boxShadow: '0 4px 16px rgba(74,144,226,0.35)',
                '&:hover': { bgcolor: colors.primary.dark },
              }}
            >
              {loading ? (
                <CircularProgress size={22} sx={{ color: 'white' }} />
              ) : (
                'Verify & continue'
              )}
            </Button>
          </Box>

          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Typography variant="body2" sx={{ color: colors.text.secondary }}>
              Didn&apos;t get the code?{' '}
              <Box
                component="button"
                type="button"
                onClick={handleResend}
                disabled={resending || cooldown > 0}
                data-testid="verify-resend"
                sx={{
                  background: 'none',
                  border: 'none',
                  p: 0,
                  cursor: cooldown > 0 ? 'default' : 'pointer',
                  color: cooldown > 0 ? colors.text.secondary : colors.primary.main,
                  fontWeight: 700,
                  fontSize: 'inherit',
                  fontFamily: 'inherit',
                  '&:hover': { textDecoration: cooldown > 0 ? 'none' : 'underline' },
                }}
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : resending ? 'Sending…' : 'Resend code'}
              </Box>
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Box
                component={Link}
                to="/login"
                sx={{
                  color: colors.text.secondary,
                  fontSize: '0.8rem',
                  textDecoration: 'none',
                  '&:hover': { color: colors.primary.main },
                }}
              >
                ← Back to Sign In
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default VerifyEmail;
